import React, { useEffect, useContext, createContext, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';

// Global, Set-based scroll lock to support nested dialogs and prevent counter drift.
const activeDialogLocks = new Set<string>();

function applyModalOpenState() {
  const anyOpen = activeDialogLocks.size > 0;
  document.documentElement.classList.toggle('modal-open', anyOpen);

  if (!anyOpen) {
    // Hard reset: clear any leftover inline overflow styles (e.g., from older builds / edge cases).
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    for (const el of Array.from(document.querySelectorAll<HTMLElement>('[data-scroll-container]'))) {
      el.style.overflow = '';
      el.style.overflowY = '';
    }
  }
}

interface DialogContextValue {
  onClose: () => void;
  isMobile: boolean;
}

const DialogContext = createContext<DialogContextValue | null>(null);

interface DialogProps {
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  children: React.ReactNode;
}

interface DialogContentProps {
  children: React.ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full';
  className?: string;
}

interface DialogHeaderProps {
  children: React.ReactNode;
  className?: string;
}

interface DialogBodyProps {
  children: React.ReactNode;
  className?: string;
}

interface DialogFooterProps {
  children: React.ReactNode;
  className?: string;
}

interface DialogTitleProps {
  children: React.ReactNode;
}

interface DialogDescriptionProps {
  children: React.ReactNode;
}

const maxWidthClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '4xl': 'max-w-4xl',
  '5xl': 'max-w-5xl',
  '6xl': 'max-w-6xl',
  full: 'max-w-[96vw] w-[96vw]',
};

export function Dialog({ open, onOpenChange, children }: DialogProps) {
  const handleClose = () => onOpenChange?.(false);
  const [isMobile, setIsMobile] = useState(false);
  const lockIdRef = useRef<string>(Math.random().toString(36).slice(2));

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (!open) return;
    activeDialogLocks.add(lockIdRef.current);
    applyModalOpenState();
    return () => {
      activeDialogLocks.delete(lockIdRef.current);
      applyModalOpenState();
    };
  }, [open]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) {
        handleClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open]);

  if (!open) return null;

  return (
    <DialogContext.Provider value={{ onClose: handleClose, isMobile }}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/55 z-50 animate-fade-in"
        onClick={handleClose}
      />
      
      {/* Dialog Container */}
      <div className={cn(
        "fixed z-60",
        isMobile ? "inset-0 flex items-end" : "inset-0 flex items-center justify-center p-4"
      )}
      >
        {children}
      </div>
    </DialogContext.Provider>
  );
}

export function DialogContent({ children, maxWidth = 'md', className }: DialogContentProps) {
  const context = useContext(DialogContext);
  
  if (!context) return null;
  
  const { isMobile, onClose } = context;
  
  return (
    <div
      className={cn(
        "bg-white dark:bg-gray-900 shadow-xl border border-gray-200 dark:border-gray-700 w-full relative flex flex-col min-h-0",
        isMobile 
          ? "rounded-t-2xl h-[calc(100vh-3.5rem)] animate-slide-up" 
          : "rounded-lg max-h-[90vh]",
        !isMobile && maxWidthClasses[maxWidth],
        // Prevent scroll chaining / overscroll bounce from affecting the page behind the dialog.
        "overscroll-contain",
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Close button */}
        <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-500 dark:text-gray-400 transition-colors"
          aria-label="Close dialog"
        >
          <X className="w-5 h-5" />
        </button>
      
      {/* Mobile swipe indicator */}
      {isMobile && (
        <div className="flex justify-center pt-2 pb-1">
          <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full" />
        </div>
      )}
      
      {children}
    </div>
  );
}

export function DialogHeader({ children, className }: DialogHeaderProps) {
  return (
    <div className={cn("flex-shrink-0 px-6 pt-6 pb-4 pr-12 border-b border-gray-200 dark:border-gray-700", className)}>
      {children}
    </div>
  );
}

export function DialogBody({ children, className }: DialogBodyProps) {
  return (
    <div className={cn("flex-1 min-h-0 overflow-y-auto px-6 py-4", className)}>
      {children}
    </div>
  );
}

export function DialogFooter({ children, className }: DialogFooterProps) {
  return (
    <div className={cn("flex-shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-2", className)}>
      {children}
    </div>
  );
}

export function DialogTitle({ children }: DialogTitleProps) {
  return (
    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
      {children}
    </h2>
  );
}

export function DialogDescription({ children }: DialogDescriptionProps) {
  return (
    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
      {children}
    </p>
  );
}

